// Building the rows of the Muscle Volume panel (#4210 Phase E).
//
// This lives in its own module, apart from `muscle-volume-helpers.ts`, because it
// is pure: it needs the muscle constants and the label lookup and nothing else.
// `muscle-volume-helpers.ts` pulls in Angular services (`LoggingService`,
// `UserDb`, `ExerciseDbService`), which cannot load under the ts-node mocha
// runner, so anything left in there is untestable. Splitting the pure part out is
// the same convention `exercise-diff.ts` follows against `exercise-diff-ui.ts`.
//
// The rows themselves used to be built by two character-identical loops, one in
// `muscle-volume-helpers.ts` and one in `workout-loader.service.ts`. Both now call
// in here, so the two displays cannot drift apart.

import { PerMuscleVolume } from '@cf-shared/data-model/fitness-plan';
import { lowerBodyResultingMuscleIds, upperBodyResultingMuscleIds } from '@cf-shared/fitness-constants';
import { MuscleId } from '@cf-shared/workout-solver-interface';
import { roundToPrecision } from '@cf-shared/shared-util';
import { getMuscleVolumeLabel } from './muscle-labels';
// type-only, so naming the service's interface here cannot create a runtime import
// cycle. the interface stays declared over there because five pages import it from
// that path.
import type { MuscleVolumeEntry } from 'services/workout-loader.service';

/**
 * Turn a per-muscle volume table into the two titled sections the Muscle Volume
 * panel shows ("Lower Body" and "Upper Body" in `cs-muscle-volume.html`).
 *
 * Shared by the two places that display the panel: `muscle-volume-helpers.ts` and
 * `workout-loader.service.ts`. They compute the volume differently (only the
 * former takes muscle-factor overrides and reports uncounted sub-slots), but the
 * rows they build out of it must stay identical.
 *
 * `shared/src/data-model/fitness-plan.test.ts` re-implements the same rule to
 * guard #3844 (abs must appear under Upper Body). It cannot call this function,
 * because it lives in `shared/` and this one reaches translated UI text, so keep
 * the two in step by hand.
 */
export function buildMuscleVolumeRows(muscleVolume: PerMuscleVolume): {
  lowerBodyMuscles: MuscleVolumeEntry[],
  upperBodyMuscles: MuscleVolumeEntry[],
} {
  return {
    lowerBodyMuscles: buildRowsForMuscleIds(lowerBodyResultingMuscleIds, muscleVolume),
    upperBodyMuscles: buildRowsForMuscleIds(upperBodyResultingMuscleIds, muscleVolume),
  };
}

/** Build one section's rows, in the order the constants list them (bigger muscles first). */
function buildRowsForMuscleIds(muscleIds: MuscleId[], muscleVolume: PerMuscleVolume): MuscleVolumeEntry[] {
  const rows: MuscleVolumeEntry[] = [];
  // for each muscle of this section, emit a row only if the plan gives it volume
  for (const muscleId of muscleIds) {
    if (muscleVolume[muscleId] === undefined) { continue; }
    rows.push({
      name: getMuscleVolumeLabel(muscleId),
      volume: roundToPrecision(muscleVolume[muscleId] || 0, 0.01),
    });
  }
  return rows;
}
